import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { IotaModalSize, MAT_DIALOG_DATA, MatDialogRef } from '@iota/iota-ui-components';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { PlatformLicenseActivationWithLicenseEntity, PlatformLicenseEntity } from 'src/app/store/entity-models';
import { platformLicenseById, selectedCompanyId, allPlatformLicenses, selectedRegulatorId } from 'src/app/store/store';
import { PlatformLicenseActivationFormComponent } from '../../components/platform-license-activation-form/platform-license-activation-form.component';
import { getValueFromStore, waitForBooleanValue } from '../../utils';
import { companyActionError, companyActionLoading } from 'src/app/store/reducer/company.reducer';
import {
    LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoiced,
    UpsertCompanyPlatformLicenseActivation
} from 'src/app/store/actions/company.actions';
import { PlatformLicenseActivationDTO, PlatformLicensePricingDTO } from '@iota/iota-management-portal-v4-client';
import { PlatformLicensePricingFormComponent } from '../../components/platform-license-pricing-form/platform-license-pricing-form.component';
import {
    LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoiced,
    UpsertCentralRegulatorPlatformLicenseActivation
} from 'src/app/store/actions/central-regulators.actions';
import { centralRegulatorsActionError, centralRegulatorsActionLoading } from 'src/app/store/reducer/central-regulators.reducer';

export enum PLATFORM_LICENSE_DIALOG_TYPE {
    ASSIGN_LICENSE,
    VIEW_LICENSE_ACTIVATION
}

export interface PlatformLicenseDialogData {
    dialogType: PLATFORM_LICENSE_DIALOG_TYPE;
    platformLicenseActivation?: PlatformLicenseActivationWithLicenseEntity;
}

enum PLATFORM_LICENSE_DIALOG_STEP {
    SELECT_LICENSE,
    ADJUST_DATES,
    ADJUST_PRICING,
    RESULT
}
const PlatformLicenseDialogStepLabels: { [K in PLATFORM_LICENSE_DIALOG_STEP]: string } = {
    [PLATFORM_LICENSE_DIALOG_STEP.SELECT_LICENSE]: $localize`Select license`,
    [PLATFORM_LICENSE_DIALOG_STEP.ADJUST_DATES]: $localize`Adjust dates`,
    [PLATFORM_LICENSE_DIALOG_STEP.ADJUST_PRICING]: $localize`Adjust pricing`,
    [PLATFORM_LICENSE_DIALOG_STEP.RESULT]: $localize`Completion`
};

@Component({
    standalone: false,
    selector: 'app-platform-license-dialog',
    templateUrl: './platform-license-dialog.component.html',
    styleUrls: ['./platform-license-dialog.component.scss']
})
export class PlatformLicenseDialogComponent implements OnInit {
    @ViewChild('platformLicenseActivationForm') licenseActivationForm: PlatformLicenseActivationFormComponent;
    @ViewChild('platformLicensePricingForm') platformLicensePricingForm: PlatformLicensePricingFormComponent;

    public modalOptions = {
        size: IotaModalSize.SIZE_7XL
    };

    stepLabels = PlatformLicenseDialogStepLabels;
    currentStep: PLATFORM_LICENSE_DIALOG_STEP = undefined;
    dateFormValid = false;
    pricingFormValid = false;

    newPlatformLicenseActivation: PlatformLicenseActivationDTO = {};
    defineCustomPricing = false;
    customPricing: PlatformLicensePricingDTO = {};

    actionLoading$ = this.store.select(companyActionLoading);
    actionError: any = undefined;

    platformLicensesThatCanBeAdded$ = this.store.select(allPlatformLicenses);

    selectedPlatformLicenseId: string;

    platformLicense$: Observable<PlatformLicenseEntity>;

    constructor(
        private dialogRef: MatDialogRef<PlatformLicenseDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: PlatformLicenseDialogData,
        private store: Store
    ) {}

    ngOnInit(): void {
        this.initDialog();
    }

    get isInSelectLicenseStep() {
        return this.currentStep === PLATFORM_LICENSE_DIALOG_STEP.SELECT_LICENSE;
    }
    get isInAdjustDatesStep() {
        return this.currentStep === PLATFORM_LICENSE_DIALOG_STEP.ADJUST_DATES;
    }
    get isInAdjustPricingStep() {
        return this.currentStep === PLATFORM_LICENSE_DIALOG_STEP.ADJUST_PRICING;
    }
    get isInResultStep() {
        return this.currentStep === PLATFORM_LICENSE_DIALOG_STEP.RESULT;
    }

    get showCancelButton() {
        return this.isInSelectLicenseStep;
    }
    get showBackButton() {
        return this.isInAdjustDatesStep || this.isInAdjustPricingStep;
    }
    get showContinueButton() {
        return this.isInAdjustDatesStep;
    }
    get showSaveButton() {
        return this.isInAdjustPricingStep;
    }

    get currentStepValid() {
        if (this.isInAdjustDatesStep) return this.dateFormValid;
        if (this.isInAdjustPricingStep) return !this.defineCustomPricing || this.pricingFormValid;

        return true;
    }

    get isViewLicenseActivationDialog() {
        return this.data?.dialogType === PLATFORM_LICENSE_DIALOG_TYPE.VIEW_LICENSE_ACTIVATION;
    }
    get isAssignLicenseDialog() {
        return this.data?.dialogType === PLATFORM_LICENSE_DIALOG_TYPE.ASSIGN_LICENSE;
    }

    get dialogTitle() {
        if (this.data?.dialogType === PLATFORM_LICENSE_DIALOG_TYPE.ASSIGN_LICENSE) {
            return $localize`Assign license to business partner`;
        } else if (this.data?.dialogType === PLATFORM_LICENSE_DIALOG_TYPE.VIEW_LICENSE_ACTIVATION) {
            return $localize`View license activation`;
        }

        return '';
    }

    initDialog() {
        if (this.data?.dialogType === PLATFORM_LICENSE_DIALOG_TYPE.ASSIGN_LICENSE) {
            this.currentStep = PLATFORM_LICENSE_DIALOG_STEP.SELECT_LICENSE;
        }
    }

    async selectLicense(platformLicenseId: string) {
        this.selectedPlatformLicenseId = platformLicenseId;
        this.platformLicense$ = this.store.select(platformLicenseById(platformLicenseId));
        this.setEffectivePricingOfLicenseToCustomPricing();
        this.currentStep = PLATFORM_LICENSE_DIALOG_STEP.ADJUST_DATES;
    }

    enablePricingEditing() {
        this.defineCustomPricing = true;
        // The effective dates are removed for custom pricing
        this.customPricing = { ...this.customPricing, effectiveFrom: undefined, effectiveTo: undefined };
    }

    async disablePricingEditing() {
        this.defineCustomPricing = false;
        this.setEffectivePricingOfLicenseToCustomPricing();
    }

    async setEffectivePricingOfLicenseToCustomPricing() {
        const selectedLicense = await getValueFromStore(this.store, platformLicenseById(this.selectedPlatformLicenseId));
        this.customPricing = { ...(selectedLicense?.currentlyEffectivePricing ?? {}) };
    }

    previousStep() {
        this.currentStep = this.currentStep - 1;
    }

    nextStep() {
        if (this.currentStep === PLATFORM_LICENSE_DIALOG_STEP.ADJUST_DATES) {
            this.newPlatformLicenseActivation = this.licenseActivationForm.getUpsertedPlatformLicenseActivation();
        }

        this.currentStep = this.currentStep + 1;
    }

    async addLicenseToBusinessPartner() {
        const companyId = await getValueFromStore(this.store, selectedCompanyId);
        const centralRegulatorId = await getValueFromStore(this.store, selectedRegulatorId);

        const licenseActivationToUpsert: PlatformLicenseActivationDTO = {
            ...this.newPlatformLicenseActivation,
            parentSkId: companyId ?? centralRegulatorId,
            platformLicenseId: this.selectedPlatformLicenseId
        };

        // Only copy the properties that can change from the custom pricing,
        // otherwise we could end up copying the IDs and other properties from the original pricing
        this.customPricing = this.platformLicensePricingForm.getUpsertedPlatformLicensePricing();
        if (this.defineCustomPricing) {
            licenseActivationToUpsert.customPlatformLicensePricing = {
                internalDescription: this.customPricing.internalDescription,
                licensePriceMonthly: this.customPricing.licensePriceMonthly,
                licensePriceYearly: this.customPricing.licensePriceYearly,
                licenseSalesTax: this.customPricing.licenseSalesTax,
                platformLicenseId: this.selectedPlatformLicenseId,
                priceIntervalsPerUser: this.customPricing.priceIntervalsPerUser
            };
        }

        this.currentStep = PLATFORM_LICENSE_DIALOG_STEP.RESULT;

        // Reload the license activations that can be deleted after a new one has been created
        if (companyId) {
            this.store.dispatch(UpsertCompanyPlatformLicenseActivation({ plaformLicenseActivation: licenseActivationToUpsert }));
            await waitForBooleanValue(this.store, companyActionLoading, false);
            this.actionError = await getValueFromStore(this.store, companyActionError);
            this.store.dispatch(LoadPlatformLicenseActivationsOfCompanyThatHaveNotBeenInvoiced({ companyId }));
        } else if (centralRegulatorId) {
            this.store.dispatch(UpsertCentralRegulatorPlatformLicenseActivation({ plaformLicenseActivation: licenseActivationToUpsert }));
            await waitForBooleanValue(this.store, centralRegulatorsActionLoading, false);
            this.actionError = await getValueFromStore(this.store, centralRegulatorsActionError);
            this.store.dispatch(LoadPlatformLicenseActivationsOfCentralRegulatorThatHaveNotBeenInvoiced({ centralRegulatorId }));
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }
}
